/**
 * Shimmer Component
 *
 * A lightweight skeleton loading effect that animates from left to right.
 * Uses pure CSS animation with no external dependencies.
 *
 * Features:
 * - Configurable width, height, and border radius
 * - Respects prefers-reduced-motion
 * - Uses design tokens for consistent styling
 */

import React from 'react';
import { tokens } from '../../styles/tokens';

export interface ShimmerProps {
  /** Width of the shimmer element (default: 100%) */
  width?: string | number;
  /** Height of the shimmer element (default: 20px) */
  height?: string | number;
  /** Border radius (default: 4px) */
  borderRadius?: string | number;
  /** Additional className for styling */
  className?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

// Unique ID for keyframes to avoid conflicts
const shimmerKeyframes = `
  @keyframes shimmer-slide {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

// Inject keyframes into document once
let keyframesInjected = false;
const injectKeyframes = () => {
  if (keyframesInjected || typeof document === 'undefined') return;

  const style = document.createElement('style');
  style.textContent = shimmerKeyframes;
  document.head.appendChild(style);
  keyframesInjected = true;
};

export const Shimmer: React.FC<ShimmerProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  className,
  'data-testid': testId = 'shimmer',
}) => {
  // Inject keyframes on first render
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
    backgroundColor: tokens.colors.gray100,
    overflow: 'hidden',
    position: 'relative',
  };

  const shimmerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(
      90deg,
      transparent 0%,
      ${tokens.colors.gray200} 50%,
      transparent 100%
    )`,
    animation: 'shimmer-slide 1.5s infinite ease-in-out',
  };

  return (
    <div
      className={className}
      data-testid={testId}
      data-multidev-sdk="true"
      role="status"
      aria-label="Loading"
      style={containerStyle}
    >
      <div style={shimmerStyle} />
    </div>
  );
};

/**
 * ShimmerSelect - A select dropdown loading placeholder
 */
export interface ShimmerSelectProps {
  /** Width of the shimmer (default: 100%) */
  width?: string | number;
  /** Test ID for testing */
  'data-testid'?: string;
}

export const ShimmerSelect: React.FC<ShimmerSelectProps> = ({
  width = '100%',
  'data-testid': testId = 'shimmer-select',
}) => {
  return (
    <Shimmer
      width={width}
      height={36}
      borderRadius={tokens.borders.radiusInput}
      data-testid={testId}
    />
  );
};

/**
 * ShimmerText - A text line loading placeholder
 */
export interface ShimmerTextProps {
  /** Width of the shimmer (default: 100%) */
  width?: string | number;
  /** Height variant */
  size?: 'small' | 'medium' | 'large';
  /** Test ID for testing */
  'data-testid'?: string;
}

export const ShimmerText: React.FC<ShimmerTextProps> = ({
  width = '100%',
  size = 'medium',
  'data-testid': testId = 'shimmer-text',
}) => {
  const heights = {
    small: 12,
    medium: 16,
    large: 20,
  };

  return (
    <Shimmer
      width={width}
      height={heights[size]}
      borderRadius={4}
      data-testid={testId}
    />
  );
};

/**
 * ShimmerBadge - A pill/badge loading placeholder (for Jira ticket badges)
 */
export interface ShimmerBadgeProps {
  /** Width of the shimmer (default: 70px) */
  width?: string | number;
  /** Test ID for testing */
  'data-testid'?: string;
}

export const ShimmerBadge: React.FC<ShimmerBadgeProps> = ({
  width = 70,
  'data-testid': testId = 'shimmer-badge',
}) => {
  return (
    <Shimmer
      width={width}
      height={24}
      borderRadius={12}
      data-testid={testId}
    />
  );
};

export default Shimmer;
